import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Check, Users, Loader2 } from 'lucide-react';
import { apiClient } from '../App';
import { resolveAvatarUrl } from '../lib/avatarUrl';

/**
 * QuickInviteDrawer - Post-join quick invite UI
 * 
 * Shows 4-6 suggested players to invite immediately after joining a game.
 * Suggestions are prefetched on Game Detail page load for instant display.
 * 
 * Features:
 * - Implicit squad logic (no formal teams/squads)
 * - One-tap invite with instant "Sent" state
 * - Context signals: "Played together recently", "Mutual follow", "Reliable player"
 */
const QuickInviteDrawer = ({ 
  open, 
  onOpenChange, 
  gameId,
  suggestions = [],
  onInviteSent
}) => {
  const [sentInvites, setSentInvites] = useState(new Set());
  const [sendingInvite, setSendingInvite] = useState(null);
  
  // Reset sent invites when drawer opens with new game
  useEffect(() => {
    if (open) {
      setSentInvites(new Set());
    }
  }, [open, gameId]);
  
  const handleInvite = async (userId) => {
    if (sentInvites.has(userId) || sendingInvite === userId) return;
    
    // Optimistically mark as sent
    setSentInvites(prev => new Set([...prev, userId]));
    setSendingInvite(userId);
    
    try {
      await apiClient.post(`/games/${gameId}/squad-invite`, {
        invited_user_id: userId
      });
      
      // Notify parent component
      if (onInviteSent) {
        onInviteSent(userId);
      }
    } catch (err) {
      // Revert on error (unless already invited)
      if (!err.response?.data?.already_invited) {
        setSentInvites(prev => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    } finally {
      setSendingInvite(null);
    }
  };
  
  // Get initials for avatar fallback
  const getInitials = (username, displayName) => {
    const name = displayName || username || '?';
    return name.charAt(0).toUpperCase();
  };
  
  // Filter out already-sent suggestions
  const availableSuggestions = suggestions.filter(s => !sentInvites.has(s.user_id));
  const hasSuggestions = suggestions.length > 0;
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="rounded-t-2xl px-6 pb-8 pt-4 max-h-[70vh]"
        data-testid="quick-invite-drawer"
      >
        {/* Handle bar */}
        <div className="w-10 h-1 bg-zinc-200 rounded-full mx-auto mb-4" />
        
        <SheetHeader className="text-left mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold text-zinc-900">
                Bring your squad
              </SheetTitle>
              <p className="text-sm text-zinc-500 mt-0.5">
                Invite players you usually run with
              </p>
            </div>
          </div>
        </SheetHeader>
        
        {!hasSuggestions ? (
          <div className="py-8 text-center">
            <p className="text-zinc-500">
              Follow more players to see suggestions here
            </p>
          </div>
        ) : (
          <div className="space-y-3 overflow-y-auto max-h-[50vh] pr-1">
            {suggestions.map((player) => {
              const isSent = sentInvites.has(player.user_id);
              const isSending = sendingInvite === player.user_id;
              
              return (
                <div 
                  key={player.user_id}
                  className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100"
                  data-testid={`suggestion-${player.user_id}`}
                >
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {player.avatar_url ? (
                      <img 
                        src={resolveAvatarUrl(player.avatar_url)} 
                        alt={player.username}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-lg font-semibold text-zinc-500">
                        {getInitials(player.username, player.display_name)}
                      </span>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-zinc-900 truncate">
                      {player.display_name || player.username}
                    </p>
                    <p className="text-sm text-zinc-500 truncate">
                      {player.context_signal}
                    </p>
                  </div>
                  
                  {/* Action Button */}
                  <Button
                    onClick={() => handleInvite(player.user_id)}
                    disabled={isSent || isSending}
                    className={`h-9 px-4 rounded-lg font-semibold text-sm transition-all duration-150 ${
                      isSent 
                        ? 'bg-green-100 text-green-700 hover:bg-green-100 cursor-default'
                        : 'bg-orange-500 hover:bg-orange-600 text-white'
                    }`}
                    data-testid={`invite-btn-${player.user_id}`}
                  >
                    {isSending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isSent ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Sent
                      </>
                    ) : (
                      'Invite'
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Done button */}
        <div className="mt-5 pt-4 border-t border-zinc-100">
          <Button
            onClick={() => onOpenChange(false)}
            variant="ghost"
            className="w-full h-11 rounded-xl font-medium text-zinc-500 hover:text-zinc-700"
            data-testid="done-btn"
          >
            {sentInvites.size > 0 
              ? `Done • ${sentInvites.size} invited`
              : 'Maybe later'
            }
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default QuickInviteDrawer;
